import { useState } from 'react';

import { LANGS, LANG_CODES, LANG_NAMES, flipDirection, parseDirection, type Lang } from '@lexio/shared';

interface Props {
  /** Aktivni smery jako klice "cs>en". */
  value: string[];
  onChange: (directions: string[]) => void;
  /** Kolik konceptu umi kazdy smer - aby slo poznat prazdnou kombinaci. */
  pairCounts: Record<string, number>;
}

/**
 * Skladani smeru zkouseni. Tohle je jadro vicejazycnosti: uzivatel si rekne
 * "chci cs->en, en->cs a en->de" a kazdy smer se pak uci nezavisle.
 *
 * Otoceni (↔) je zamerne jedno kliknuti - je to nejcastejsi operace.
 */
export function DirectionPicker({ value, onChange, pairCounts }: Props) {
  const [from, setFrom] = useState<Lang>('cs');
  const [to, setTo] = useState<Lang>('en');

  const add = (key: string) => {
    if (!value.includes(key)) onChange([...value, key]);
  };

  const remove = (key: string) => {
    // Alespon jeden smer musi zustat, jinak by fronta byla trvale prazdna.
    if (value.length > 1) onChange(value.filter((d) => d !== key));
  };

  /** Otoci smer na miste - zachova poradi v seznamu. */
  const flip = (key: string) => {
    const dir = parseDirection(key);
    if (!dir) return;
    const flipped = `${dir.to}>${dir.from}`;
    if (value.includes(flipped)) {
      // Uz tam je - jen puvodni odebereme, at nevznikne duplicita.
      remove(key);
      return;
    }
    onChange(value.map((d) => (d === key ? flipped : d)));
  };

  const candidate = `${from}>${to}`;
  const candidateCount = pairCounts[candidate] ?? 0;
  const canAdd = from !== to && !value.includes(candidate);

  return (
    <div className="dir-picker">
      <ul className="dir-list">
        {value.map((key) => {
          const dir = parseDirection(key);
          if (!dir) return null;
          const count = pairCounts[key] ?? 0;

          return (
            <li key={key} className="dir-item">
              <div className="dir-labels">
                <span className="dir-lang">{LANG_CODES[dir.from]}</span>
                <span className="dir-arrow">→</span>
                <span className="dir-lang">{LANG_CODES[dir.to]}</span>
                <span className="dir-desc muted small">
                  {LANG_NAMES[dir.from]} → {LANG_NAMES[dir.to]}
                </span>
              </div>

              <div className="dir-actions">
                <span className={`dir-count ${count === 0 ? 'empty' : ''}`}>
                  {count === 0 ? 'zatím prázdné' : `${count} slov`}
                </span>
                <button
                  className="btn ghost small"
                  onClick={() => flip(key)}
                  title="Otočit směr"
                  aria-label={`Otočit ${LANG_CODES[dir.from]} → ${LANG_CODES[dir.to]}`}
                >
                  ↔
                </button>
                <button
                  className="btn ghost small"
                  onClick={() => remove(key)}
                  disabled={value.length <= 1}
                  title={value.length <= 1 ? 'Aspoň jeden směr musí zůstat' : 'Odebrat'}
                  aria-label="Odebrat směr"
                >
                  ✕
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="dir-add">
        <select className="input small" value={from} onChange={(e) => setFrom(e.target.value as Lang)}>
          {LANGS.map((l) => (
            <option key={l} value={l}>
              {LANG_NAMES[l]}
            </option>
          ))}
        </select>

        <button
          className="btn ghost small flip-new"
          onClick={() => {
            const d = flipDirection({ from, to });
            setFrom(d.from);
            setTo(d.to);
          }}
          title="Prohodit"
        >
          ↔
        </button>

        <select className="input small" value={to} onChange={(e) => setTo(e.target.value as Lang)}>
          {LANGS.map((l) => (
            <option key={l} value={l}>
              {LANG_NAMES[l]}
            </option>
          ))}
        </select>

        <button className="btn small" onClick={() => add(candidate)} disabled={!canAdd}>
          Přidat
        </button>
      </div>

      {from === to ? (
        <p className="muted small">Vyber dva různé jazyky.</p>
      ) : value.includes(candidate) ? (
        <p className="muted small">Tenhle směr už máš zapnutý.</p>
      ) : (
        <p className="muted small">
          {candidateCount === 0
            ? `Pro ${LANG_NAMES[from]} → ${LANG_NAMES[to]} zatím není žádný obsah.`
            : `${LANG_NAMES[from]} → ${LANG_NAMES[to]}: ${candidateCount} slov k dispozici.`}
        </p>
      )}
    </div>
  );
}
